import { NavLink } from 'react-router-dom'
import { Pin, PinOff } from 'lucide-react'
import { Tooltip } from 'radix-ui'
import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/lib/cn'
import { NAV, moduleForPath, type NavSection } from '@/lib/nav'
import { useQueries, useQuery } from '@tanstack/react-query'
import { getMyModules } from '@/api/settings'
import { approvalKeys, fetchMyEmployeeId, sourcesFor } from '@/api/approvals'
import { useOrgBranding } from './BrandingApplier'
import { apiAssetUrl } from '@/api/baseUrl'

/**
 * Pending approvals the caller can act on (own requests excluded) — the number on the
 * Approvals menu item. Shares the Approvals page's queries (same keys), so opening the page
 * or deciding an item updates the badge without extra requests.
 */
export function useApprovalsCount(): number {
  const { user, hasRole } = useAuth()
  const approver = !!user && hasRole(['HR_ADMIN', 'HR_EXECUTIVE', 'MANAGER'])
  const sources = useMemo(() => (approver ? sourcesFor(hasRole) : []), [approver, hasRole])
  const results = useQueries({
    queries: sources.map((src) => ({
      queryKey: approvalKeys.source(src.type),
      queryFn: src.fetch,
      staleTime: 60_000,
      refetchInterval: 120_000,
      meta: { inlineError: true },
      enabled: approver,
    })),
  })
  const me = useQuery({ queryKey: ['approvals', 'me'], queryFn: fetchMyEmployeeId, staleTime: 10 * 60_000, enabled: approver, meta: { inlineError: true } })
  return results.flatMap((r) => r.data ?? []).filter((i) => !me.data || i.employeeId !== me.data).length
}

const STORAGE_KEY = 'bigroi.sidebar.pinned'
const ALL_PATHS = NAV.flatMap((s) => s.items.map((i) => i.to))
const NESTED_PARENTS = new Set(ALL_PATHS.filter((p) => ALL_PATHS.some((q) => q !== p && q.startsWith(p + '/'))))
/** Section → module (Leave sits in Time but carries the leave amber). Chip colours come from the --sb-* tokens. */
const TONE_CHIP = {
  people: 'bg-[rgb(var(--sb-people-bg)/var(--sb-chip-a))] text-[rgb(var(--sb-people-fg))]',
  time: 'bg-[rgb(var(--sb-time-bg)/var(--sb-chip-a))] text-[rgb(var(--sb-time-fg))]',
  leave: 'bg-[rgb(var(--sb-leave-bg)/var(--sb-chip-a))] text-[rgb(var(--sb-leave-fg))]',
  payroll: 'bg-[rgb(var(--sb-payroll-bg)/var(--sb-chip-a))] text-[rgb(var(--sb-payroll-fg))]',
  compliance: 'bg-[rgb(var(--sb-compliance-bg)/var(--sb-chip-a))] text-[rgb(var(--sb-compliance-fg))]',
  engage: 'bg-[rgb(var(--sb-engage-bg)/var(--sb-chip-a))] text-[rgb(var(--sb-engage-fg))]',
} as const
const NAV_TONE: Record<string, keyof typeof TONE_CHIP> = {
  Overview: 'people', People: 'people', Time: 'time', Payroll: 'payroll', Compliance: 'compliance',
  Workplace: 'engage', Insights: 'time', 'My space': 'leave',
}
const navTone = (section: string, to: string): keyof typeof TONE_CHIP =>
  to === '/leave' || to === '/holidays' ? 'leave' : NAV_TONE[section] ?? 'people'
const EXPANDED_W = 'w-[248px]'
const COLLAPSED_W = 'w-[72px]'

/** Sections filtered to what the current user's role can open and their organisation has switched on. */
export function useVisibleNav(): NavSection[] {
  const { user, hasRole } = useAuth()
  // A failed lookup hides nothing: the API still blocks switched-off modules.
  const mods = useQuery({ queryKey: ['my-modules'], queryFn: getMyModules, enabled: !!user, staleTime: 60_000, retry: false })
  const disabled = useMemo(() => new Set(mods.data?.disabled ?? []), [mods.data])
  return useMemo(
    () =>
      NAV.map((s) => ({
        ...s,
        items: s.items.filter((i) => {
          if (i.audience && !(user && i.audience.includes(user.role))) return false
          if (i.roles && !(user && hasRole(i.roles as never[]))) return false
          const m = moduleForPath(i.to)
          return !m || !disabled.has(m)
        }),
      })).filter((s) => s.items.length > 0),
    [user, hasRole, disabled],
  )
}

/** The nav list — shared by the desktop rail and the mobile drawer. */
export function NavContent({ expanded, onNavigate }: { expanded: boolean; onNavigate?: () => void }) {
  const sections = useVisibleNav()
  const pendingApprovals = useApprovalsCount()
  const badges: Record<string, number> = { '/approvals': pendingApprovals }
  return (
    <nav aria-label="Main" className="flex-1 overflow-y-auto overflow-x-hidden px-3 pb-4 [scrollbar-color:rgb(var(--sb-label)/0.35)_transparent]">
      {sections.map((section, si) => (
        <div key={section.title}>
          {expanded ? (
            <div className="truncate px-2.5 pb-1.5 pt-5 text-[11px] font-medium tracking-[0.02em] text-[rgb(var(--sb-label))]">
              {section.title}
            </div>
          ) : (
            si > 0 && <div aria-hidden className="mx-2 my-2.5 h-px bg-[rgb(var(--sb-line))]" />
          )}
          <ul className="space-y-0.5">
            {section.items.map((it) => {
              const link = (
                <NavLink
                  to={it.to}
                  // Exact match when another destination nests under this one (/admin,
                  // /attendance → /attendance/regularize), so only one item is active.
                  end={NESTED_PARENTS.has(it.to)}
                  onClick={onNavigate}
                  aria-label={expanded ? (badges[it.to] ? `${it.label}, ${badges[it.to]} pending` : undefined) : (badges[it.to] ? `${it.label}, ${badges[it.to]} pending` : it.label)}
                  className={({ isActive }) =>
                    cn(
                      'group relative flex h-9 items-center gap-3 rounded-lg text-[13px] outline-none',
                      'transition-colors duration-fast ease-standard',
                      'focus-visible:ring-2 focus-visible:ring-[rgb(var(--sb-ring))]',
                      expanded ? 'px-1.5' : 'justify-center',
                      isActive
                        ? 'bg-[rgb(var(--sb-active-bg))] font-medium text-[rgb(var(--sb-active-text))]'
                        : 'font-normal text-[rgb(var(--sb-text))] hover:bg-[rgb(var(--sb-hover))]',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={cn(
                          'relative grid size-7 shrink-0 place-items-center rounded-lg transition-colors duration-fast ease-standard',
                          isActive ? 'bg-[rgb(var(--sb-chip-active))] text-white' : TONE_CHIP[navTone(section.title, it.to)],
                        )}
                      >
                        <it.icon className="size-[16px]" />
                        {!expanded && badges[it.to] > 0 && (
                          <span aria-hidden className="absolute -right-1 -top-1 size-2.5 rounded-full bg-[rgb(var(--sb-badge))] ring-2 ring-[rgb(var(--sb-bg))]" />
                        )}
                      </span>
                      {expanded && <span className="flex-1 truncate">{it.label}</span>}
                      {expanded && badges[it.to] > 0 && (
                        <span data-testid={`nav-badge-${it.to}`} className="num ml-auto rounded-full bg-[rgb(var(--sb-badge))] px-1.5 py-px text-[11px] font-semibold leading-4 text-white">
                          {badges[it.to] > 99 ? '99+' : badges[it.to]}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              )
              return (
                <li key={it.to}>
                  {expanded ? (
                    link
                  ) : (
                    <Tooltip.Root delayDuration={150}>
                      <Tooltip.Trigger asChild>{link}</Tooltip.Trigger>
                      <Tooltip.Portal>
                        <Tooltip.Content
                          side="right"
                          sideOffset={10}
                          className="z-[90] rounded-md bg-tooltip px-2.5 py-1.5 text-xs font-medium text-tooltip-foreground shadow-e3 data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0"
                        >
                          {it.label}
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </nav>
  )
}

/** The organisation's logo and name (Settings → Branding); BIGROI's mark when none is set. */
export function BrandMark({ expanded }: { expanded: boolean }) {
  const b = useOrgBranding().data
  const [broken, setBroken] = useState(false)
  const [wide, setWide] = useState(false)
  const logo = b?.logo_url && !broken ? b.logo_url : null
  const name = b?.name?.trim() || 'BIGROI'
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      {/* 36px tile: legible at the rail's 64px width (was 32px, unreadable for real logos). */}
      {logo ? (
        <img
          data-keep-white
          src={apiAssetUrl(logo) ?? undefined}
          alt=""
          onError={() => setBroken(true)}
          // A wide lockup (emblem + wordmark, e.g. 480x120) shrunk whole into a square is an
          // unreadable sliver; show its leading emblem at full tile height instead.
          onLoad={(e) => setWide(e.currentTarget.naturalWidth > e.currentTarget.naturalHeight * 1.6)}
          className={cn('size-8 shrink-0 rounded-lg bg-white ring-1 ring-[rgb(var(--sb-line))]', wide ? 'object-cover object-left' : 'object-contain p-0.5')}
        />
      ) : (
        <div className="grid size-8 shrink-0 place-items-center rounded-lg bg-[#0F6E56] text-base font-semibold tracking-tight text-white">
          {name[0].toUpperCase()}
        </div>
      )}
      {expanded && (
        // The full organisation name on up to two lines; longer names end in an ellipsis and
        // carry the whole name in the tooltip. The product subtitle is dropped for a named
        // organisation so two lines fit the 56px header.
        <div className="min-w-0">
          <div className="line-clamp-2 break-words text-[13px] font-medium leading-[1.25] text-[rgb(var(--sb-brand))]" title={name}>{name}</div>
          {!b?.name && <div className="mt-0.5 truncate text-[11px] font-medium text-[rgb(var(--sb-label))]">HRMS</div>}
        </div>
      )}
    </div>
  )
}

/**
 * Desktop rail. Collapsed it is a 72px icon rail; on hover OR keyboard focus it expands
 * OVER the page (no reflow of the main column); pinned, it takes 248px in the layout.
 * Hidden below md — the mobile drawer (Topbar) takes over there.
 */
export function Sidebar() {
  const [pinned, setPinned] = useState<boolean>(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1' } catch { return false }
  })
  const [peek, setPeek] = useState(false)

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, pinned ? '1' : '0') } catch { /* storage unavailable */ }
  }, [pinned])

  const expanded = pinned || peek

  return (
    <Tooltip.Provider>
      <div data-print-hide className={cn('relative hidden shrink-0 transition-[width] duration-slow ease-emphasized md:block print:hidden', pinned ? EXPANDED_W : COLLAPSED_W)}>
        <aside
          onMouseEnter={() => setPeek(true)}
          onMouseLeave={() => setPeek(false)}
          onFocus={() => setPeek(true)}
          onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setPeek(false) }}
          className={cn(
            // z-[45]: above the Topbar (z-40), whose translucent bar otherwise covered the
            // expanded (hover) rail's brand header; below menus (z-50) and dialogs (z-60+).
            'absolute inset-y-0 left-0 z-[45] flex flex-col overflow-hidden bg-[rgb(var(--sb-bg))] border-r border-[rgb(var(--sb-line))]',
            'transition-[width,box-shadow] duration-slow ease-emphasized',
            expanded ? EXPANDED_W : COLLAPSED_W,
            peek && !pinned && 'shadow-e4',
          )}
        >
          <div className={cn('flex h-14 shrink-0 items-center border-b border-[rgb(var(--sb-line))]', expanded ? 'justify-between px-4' : 'justify-center')}>
            <BrandMark expanded={expanded} />
            {expanded && (
              <button
                type="button"
                onClick={() => setPinned((p) => !p)}
                className="grid size-7 place-items-center rounded-md text-[rgb(var(--sb-label))] transition-colors duration-fast hover:bg-[rgb(var(--sb-hover))] hover:text-[rgb(var(--sb-brand))] focus-visible:ring-2 focus-visible:ring-[rgb(var(--sb-ring))]"
                aria-label={pinned ? 'Unpin sidebar' : 'Pin sidebar open'}
                aria-pressed={pinned}
                title={pinned ? 'Unpin sidebar' : 'Pin sidebar open'}
              >
                {pinned ? <PinOff className="size-4" /> : <Pin className="size-4" />}
              </button>
            )}
          </div>
          <NavContent expanded={expanded} />
        </aside>
      </div>
    </Tooltip.Provider>
  )
}
